import { createFileRoute, Navigate, useSearch } from '@tanstack/react-router'
import { useEffect } from 'react'

import { useIsAuthenticated } from '@/hooks'
import { setAuthToken } from '@/lib/auth-token'

/**
 * 获取 Slack OAuth URL
 * 生产环境使用完整的 API Worker URL，开发环境使用相对路径
 */
function getSlackAuthUrl(): string {
  // 生产环境强制使用 Service Binding (通过相对路径)
  const baseUrl = '/api/v1/auth/slack'

  if (typeof window !== 'undefined') {
    // 显式指定重定向 URL，确保跳回当前域名
    const redirectUrl = `${window.location.origin}/login`
    return `${baseUrl}?redirect_url=${encodeURIComponent(redirectUrl)}`
  }

  return baseUrl
}

/**
 * 登录页路由
 * - 已认证用户重定向到仪表盘
 * - 未认证用户显示登录页面
 * - 处理 OAuth 回调的 token 参数
 */
export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: search.token as string | undefined,
    error: search.error as string | undefined,
  }),
})

function LoginPage() {
  const { isAuthenticated, isLoading } = useIsAuthenticated()
  const search = useSearch({ from: '/login' })
  const token = search.token
  const error = search.error

  // 处理 OAuth 回调的 token
  useEffect(() => {
    if (token) {
      setAuthToken(token)
      // 清除 URL 中的 token 参数并重定向
      window.location.href = '/dashboard'
    }
  }, [token])

  if (isLoading || token) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-muted-foreground">加载中...</div>
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" />
  }

  return (
    <div className="bg-background flex min-h-screen items-center justify-center">
      <div className="w-full max-w-md space-y-8 px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold tracking-tight">IT 开发公司内部管理系统</h1>
          <p className="text-muted-foreground mt-2 text-sm">请使用 Slack 账号登录</p>
        </div>

        {error && (
          <div className="border-destructive/50 bg-destructive/10 text-destructive rounded-lg border p-4 text-center text-sm">
            {error === 'employee_not_found' && '未找到关联的员工账号，请联系管理员'}
            {error === 'employee_inactive' && '您的账号已被禁用，请联系管理员'}
            {error === 'oauth_failed' && 'Slack 授权失败，请重试'}
            {error === 'session_expired' && '会话已过期，请重新登录'}
            {error === 'auth_check_failed' && '认证检查失败，请重新登录'}
            {![
              'employee_not_found',
              'employee_inactive',
              'oauth_failed',
              'session_expired',
              'auth_check_failed',
            ].includes(error) && `登录失败: ${error}`}
          </div>
        )}

        <div className="bg-card rounded-lg border p-6 shadow-sm">
          <a
            href={getSlackAuthUrl()}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-[#4A154B] px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-[#3e1240]"
          >
            <SlackIcon className="h-5 w-5" />
            使用 Slack 登录
          </a>
        </div>

        <p className="text-muted-foreground text-center text-xs">如有问题，请联系 IT 部门</p>
      </div>
    </div>
  )
}

/**
 * Slack 图标
 */
function SlackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
    </svg>
  )
}
